import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import LatestAnnouncementCard from "@/components/LatestAnnouncementCard";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Briefcase, Clock, Video, ClipboardList, CheckCircle2, AlertCircle,
  LogIn, LogOut, Plus, ArrowRight, TrendingUp, CalendarDays,
} from "lucide-react";

function StatCard({ label, value, hint, icon: Icon }) {
  return (
    <div className="kb-card p-4">
      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center gap-1.5">
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {label}
      </div>
      <div className="font-serif text-3xl text-kb-ink mt-2">{value}</div>
      {hint && <div className="kb-mono text-[11px] text-kb-ink-muted mt-1">{hint}</div>}
    </div>
  );
}

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [previewTask, setPreviewTask] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/employee/tasks").then((r) => setTasks(r.data || [])).catch(() => {}),
      api.get("/employee/attendance?days=7").then((r) => setAttendance(r.data || [])).catch(() => {}),
      api.get("/employee/meetings").then((r) => setMeetings(r.data || [])).catch(() => {}),
      api.get("/intern/logs/mine?days=7").then((r) => setLogs(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const clock = async (action) => {
    setClocking(true);
    try {
      await api.post("/intern/clock", { action });
      toast.success(action === "in" ? "Clocked in" : "Clocked out");
      // Refresh attendance row for today
      api.get("/employee/attendance?days=7").then((r) => setAttendance(r.data || []));
    } catch (e) { toast.error("Couldn't update clock"); }
    finally { setClocking(false); }
  };

  // Drag-and-drop for the mini-kanban (mirrors EmployeeKanban.jsx behaviour).
  const moveTask = async (t, newStatus) => {
    if (t.status === newStatus) return;
    setTasks((arr) => arr.map((x) => x.id === t.id ? { ...x, status: newStatus } : x));
    try { await api.put(`/employee/tasks/${t.id}`, { status: newStatus }); }
    catch { toast.error("Couldn't update"); }
  };
  const onDragStart = (e, t) => {
    e.dataTransfer.setData("text/plain", t.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (e, colKey) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const t = tasks.find((x) => x.id === id);
    if (t) moveTask(t, colKey);
  };

  const today = new Date().toISOString().slice(0, 10);
  const todayAtt = attendance.find((d) => d.date === today);
  const minutesThisWeek = attendance.reduce((s, d) => s + (d.minutes_online || 0), 0);
  const inProgressTasks = tasks.filter((t) => t.status === "in_progress").length;
  const todoTasks = tasks.filter((t) => t.status === "todo").length;
  const doneTasks = tasks.filter((t) => t.status === "done").length;
  const overdue = tasks.filter((t) => t.due_date && t.status !== "done" && t.due_date < today).length;
  const upcomingMeetings = meetings.filter((m) => !m.when || new Date(m.when) >= new Date()).slice(0, 4);
  const recentTasks = tasks.filter((t) => t.status !== "done").slice(0, 5);

  return (
    <div data-testid="employee-dashboard">
      {/* ---- Header + Clock-in / Clock-out ---- */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EMPLOYEE WORKSPACE</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
            Welcome, <em className="italic text-kb-primary">{user?.name?.split(" ")[0] || "Team"}</em>.
          </h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-xl">
            Your day at a glance — open tasks, attendance, and what&apos;s coming up.
            {user?.employee_role ? <> Current role: <strong>{user.employee_role}</strong>.</> : null}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => clock("in")} disabled={clocking} className="kb-btn-outline text-sm" data-testid="employee-clock-in-btn">
            <LogIn className="w-4 h-4" /> Clock in
          </button>
          <button onClick={() => clock("out")} disabled={clocking} className="kb-btn-outline text-sm" data-testid="employee-clock-out-btn">
            <LogOut className="w-4 h-4" /> Clock out
          </button>
          <Link to="/dashboard/employee/kanban" className="kb-btn-primary text-sm" data-testid="employee-open-kanban">
            <Briefcase className="w-4 h-4" /> Open Kanban
          </Link>
        </div>
      </div>

      {/* ---- Latest announcement banner ---- */}
      <div className="mt-6"><LatestAnnouncementCard /></div>

      {/* ---- Quick stats (employee-focused) ---- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
        <StatCard label="Active tasks" value={todoTasks + inProgressTasks} hint={`${todoTasks} to do · ${inProgressTasks} in progress`} icon={Briefcase} />
        <StatCard label="Completed" value={doneTasks} hint={overdue > 0 ? `⚠ ${overdue} overdue` : "On track"} icon={CheckCircle2} />
        <StatCard label="This week" value={`${Math.floor(minutesThisWeek / 60)}h ${minutesThisWeek % 60}m`} hint="Time on platform" icon={Clock} />
        <StatCard label="Daily logs" value={`${logs.length} / 7`} hint="Last 7 days" icon={ClipboardList} />
      </div>

      {/* ---- Today's status (clock + meetings) ---- */}
      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="kb-card p-5">
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">TODAY · {today}</div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="kb-mono text-[10px] text-kb-ink-muted">Clock in</div>
              <div className="font-mono text-kb-ink">{todayAtt?.clock_in ? new Date(todayAtt.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
            </div>
            <div>
              <div className="kb-mono text-[10px] text-kb-ink-muted">Clock out</div>
              <div className="font-mono text-kb-ink">{todayAtt?.clock_out ? new Date(todayAtt.clock_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
            </div>
            <div>
              <div className="kb-mono text-[10px] text-kb-ink-muted">Time online</div>
              <div className="font-mono text-kb-primary">{todayAtt ? `${Math.floor(todayAtt.minutes_online / 60)}h ${todayAtt.minutes_online % 60}m` : "0m"}</div>
            </div>
            <div>
              <Link to="/dashboard/employee/attendance" className="kb-mono text-[10px] uppercase text-kb-primary inline-flex items-center gap-1">
                Full history <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>

        <div className="kb-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">UPCOMING MEETINGS</div>
            <Link to="/dashboard/employee/meetings" className="kb-mono text-[10px] uppercase text-kb-primary inline-flex items-center gap-1">All meetings <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {upcomingMeetings.length === 0 ? (
            <div className="mt-3 text-sm text-kb-ink-muted">No upcoming meetings. <Link to="/dashboard/employee/meetings" className="text-kb-primary">Create one →</Link></div>
          ) : (
            <div className="mt-3 space-y-2">
              {upcomingMeetings.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-sm px-3 py-2" data-testid={`dashboard-meeting-${m.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-kb-ink truncate">{m.title}</div>
                    <div className="kb-mono text-[11px] text-kb-ink-muted">{m.when ? new Date(m.when).toLocaleString() : "Instant"}</div>
                  </div>
                  {m.meet_url && (
                    <a href={m.meet_url} target="_blank" rel="noreferrer" className="kb-btn-primary text-xs">
                      <Video className="w-3.5 h-3.5" /> Join
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---- Mini Kanban preview on the dashboard ---- */}
      <div className="mt-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-serif text-xl text-kb-ink">Your tasks</h2>
          <div className="flex gap-2">
            <Link to="/dashboard/employee/assigned-projects" className="kb-mono text-[10px] uppercase text-kb-primary inline-flex items-center gap-1">
              Assigned projects <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/dashboard/employee/kanban" className="kb-mono text-[10px] uppercase text-kb-primary inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> New task
            </Link>
          </div>
        </div>
        {loading ? <div className="mt-3 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
          tasks.length === 0 ? (
            <div className="mt-3 text-center py-10 border border-dashed border-slate-300 rounded-sm bg-white">
              <Briefcase className="w-7 h-7 text-kb-ink-light mx-auto" strokeWidth={1.5} />
              <p className="mt-2 text-sm text-kb-ink-muted">No tasks yet. <Link to="/dashboard/employee/kanban" className="text-kb-primary">Add one →</Link></p>
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { key: "todo", label: "To do" },
                { key: "in_progress", label: "In progress" },
                { key: "review", label: "Review" },
                { key: "done", label: "Done" },
              ].map((col) => {
                const colTasks = tasks.filter((t) => t.status === col.key).slice(0, 4);
                return (
                  <div
                    key={col.key}
                    className="bg-slate-50 border border-slate-200 rounded-sm p-2.5"
                    onDragOver={onDragOver}
                    onDrop={(e) => onDrop(e, col.key)}
                    data-testid={`dashboard-kanban-col-${col.key}`}
                  >
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center justify-between px-1 mb-2">
                      <span>{col.label}</span>
                      <span className="bg-white border border-slate-200 rounded px-1.5">{tasks.filter((t) => t.status === col.key).length}</span>
                    </div>
                    <div className="space-y-1.5">
                      {colTasks.map((t) => (
                        <div
                          key={t.id}
                          draggable
                          onDragStart={(e) => onDragStart(e, t)}
                          onClick={() => setPreviewTask(t)}
                          className="block bg-white p-2.5 border border-slate-200 rounded-sm hover:border-kb-primary transition-colors cursor-grab active:cursor-grabbing"
                          data-testid={`dashboard-task-${t.id}`}
                        >
                          <div className="text-xs font-medium text-kb-ink leading-snug">{t.title}</div>
                          <div className="kb-mono text-[10px] text-kb-ink-muted mt-1 flex flex-wrap gap-x-2">
                            <span>{t.priority}</span>
                            {t.due_date && <span>· {t.due_date}</span>}
                            <span className="text-kb-primary">· preview</span>
                          </div>
                        </div>
                      ))}
                      {colTasks.length === 0 && <div className="text-center py-3 text-[10px] text-kb-ink-light italic">—</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {/* ---- Productivity + quick links ---- */}
      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="kb-card p-5">
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> PRODUCTIVITY · LAST 7 DAYS</div>
          <div className="mt-3 grid grid-cols-7 gap-1.5 h-20 items-end">
            {Array.from({ length: 7 }).map((_, i) => {
              const d = new Date(); d.setDate(d.getDate() - (6 - i));
              const ds = d.toISOString().slice(0, 10);
              const att = attendance.find((x) => x.date === ds);
              const mins = att?.minutes_online || 0;
              const h = Math.min(100, (mins / 480) * 100); // 8h = 100%
              return (
                <div key={ds} className="flex flex-col items-center gap-1">
                  <div className="w-full bg-slate-100 rounded-sm flex-1 flex items-end">
                    <div className="w-full bg-kb-primary rounded-sm transition-all" style={{ height: `${h}%` }} />
                  </div>
                  <div className="kb-mono text-[9px] text-kb-ink-light">{ds.slice(8)}</div>
                </div>
              );
            })}
          </div>
          <div className="kb-mono text-[10px] text-kb-ink-muted mt-2">Bars scale to an 8-hour workday.</div>
        </div>

        <div className="kb-card p-5">
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center gap-1.5"><CalendarDays className="w-3 h-3" /> RECENT DAILY LOGS</div>
          {logs.length === 0 ? (
            <div className="mt-3 text-sm text-kb-ink-muted">No logs yet. <Link to="/dashboard/intern/daily-log" className="text-kb-primary">Add today&apos;s →</Link></div>
          ) : (
            <ul className="mt-3 space-y-2">
              {logs.slice(0, 4).map((l) => (
                <li key={l.id} className="text-sm">
                  <span className="kb-mono text-[10px] text-kb-ink-muted">{l.date}</span>
                  <span className="ml-2 text-kb-ink">{(l.summary || "").slice(0, 80)}{(l.summary || "").length > 80 ? "…" : ""}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/dashboard/intern/daily-log" className="mt-3 kb-mono text-[10px] uppercase text-kb-primary inline-flex items-center gap-1">Write today&apos;s log <ArrowRight className="w-3 h-3" /></Link>
        </div>
      </div>

      {overdue > 0 && (
        <div className="mt-6 border border-rose-200 bg-rose-50 rounded-sm px-4 py-3 flex items-center gap-3 text-sm text-rose-900" data-testid="overdue-banner">
          <AlertCircle className="w-4 h-4 shrink-0" />
          You have <strong>{overdue}</strong> overdue task{overdue === 1 ? "" : "s"}. <Link to="/dashboard/employee/kanban" className="font-medium underline">Review them →</Link>
        </div>
      )}
      {previewTask && (
        <TaskPreviewModal task={previewTask} onClose={() => setPreviewTask(null)} />
      )}
    </div>
  );
}

/** Quick-preview modal for a task card on the dashboard. Shows the full
 * markdown description + meta and offers a "Go to workspace" jump so users
 * see what they're jumping into before landing on a heavy workspace page. */
function TaskPreviewModal({ task, onClose }) {
  const openWorkspace = async () => {
    if (task.guidance_id) { window.location.href = `/dashboard/tasks/${task.guidance_id}`; return; }
    try {
      const r = await api.post(`/employee/tasks/${task.id}/workspace`);
      if (r.data?.guidance_id) window.location.href = `/dashboard/tasks/${r.data.guidance_id}`;
    } catch { window.location.href = "/dashboard/employee/kanban"; }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="dashboard-task-preview">
      <div className="bg-white rounded-sm max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">TASK</div>
            <h2 className="font-serif text-xl text-kb-ink mt-0.5">{task.title}</h2>
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
              {task.priority}{task.due_date ? ` · Due ${task.due_date}` : ""}{task.status ? ` · ${task.status}` : ""}
            </div>
          </div>
          <button onClick={onClose} className="text-kb-ink-muted hover:text-kb-primary text-sm" data-testid="task-preview-close">✕</button>
        </div>
        <div className="p-5">
          {task.description ? (
            <div className="kb-task-md leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{task.description}</ReactMarkdown>
            </div>
          ) : (
            <div className="text-sm text-kb-ink-muted italic">No description provided.</div>
          )}
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="kb-btn-secondary text-sm">Close</button>
          <button onClick={openWorkspace} className="kb-btn-primary text-sm" data-testid="task-preview-open-workspace">Go to workspace →</button>
        </div>
      </div>
    </div>
  );
}
